import {
  HStack,
  MenuButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  VStack,
} from '@chakra-ui/react';
import { Button, Checkbox } from 'Atoms';
import { Menu } from 'Molecules/Menu';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Typography } from 'Tokens';
import { ChevronDownIcon } from 'Tokens/Icons/Direction';
import { CheckIcon } from 'Tokens/Icons/Status';

export const MetricsFilter = ({
  title,
  options,
  menuListWidth,
  isSingleSelect = true,
  setConditions,
  clearFilters,
  setClearFilters,
}: {
  title: string;
  options: { label: string; ref: string }[];
  menuListWidth?: string;
  isSingleSelect?: boolean;
  setConditions: Dispatch<SetStateAction<string[]>>;
  clearFilters: boolean;
  setClearFilters: Dispatch<SetStateAction<boolean>>;
}) => {
  const [singleSelect, setSingleSelect] = useState('');
  const [multiSelect, setMultiSelect] = useState<string[]>([]);
  const [isClicked, setIsClicked] = useState(false);

  useEffect(() => {
    if (clearFilters) {
      setSingleSelect('');
      setMultiSelect([]);
      setClearFilters(false);
    }
  }, [clearFilters]);

  return isSingleSelect ? (
    <Menu
      closeOnSelect={false}
      menuListWidth={menuListWidth}
      placement="bottom-end"
      customMenuButton={
        <MenuButton
          as={Button}
          border={'none'}
          rightIcon={<ChevronDownIcon color={singleSelect ? 'text.selected' : 'text.muted'} />}
          variant={'ghost'}
          selected={!!singleSelect}
        >
          {title}
        </MenuButton>
      }
      sections={[
        {
          actions: options.map((option) => ({
            id: option.ref,
            render: () => {
              return (
                <HStack justifyContent={'space-between'} width={'100%'}>
                  <Typography
                    variant="bodyStrong"
                    color={singleSelect === option.ref ? 'text.selected' : 'text.muted'}
                  >
                    {option.label}
                  </Typography>
                  {singleSelect === option.ref ? <CheckIcon color={'text.selected'} /> : <></>}
                </HStack>
              );
            },
            onClick: () => {
              setSingleSelect(option.ref);
              setConditions((prev) => {
                return [option.ref, ...prev.slice(1)];
              });
            },
          })),
        },
      ]}
    ></Menu>
  ) : (
    <Popover placement="bottom-end" matchWidth onClose={() => setIsClicked(false)}>
      <PopoverTrigger>
        <Button
          border={'none'}
          rightIcon={<ChevronDownIcon color={singleSelect ? 'text.selected' : 'text.muted'} />}
          variant={'ghost'}
          selected={!!multiSelect.length}
          onClick={() => setIsClicked(!isClicked)}
          background={isClicked ? 'bg.pressed' : 'bg.default'}
          _hover={{ bg: 'bg.pressed' }}
        >
          {title} {multiSelect.length ? `(${multiSelect.length})` : ''}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        border="none"
        boxShadow=" 0px 0px 24px -2px #0F0F2E1F"
        borderRadius="10px"
        width={'inherit'}
        overflow={'auto'}
      >
        <PopoverHeader paddingBottom={'0px'} paddingTop={'0px'} borderBottom={'none'}>
          <HStack justifyContent={'space-between'}>
            <Typography variant="h4">Show</Typography>
            <Button
              padding={'0px 5px'}
              border={'none'}
              onClick={() => {
                setMultiSelect([]);
                setConditions((prev) => [prev[0]]);
              }}
            >
              <Typography variant="bodyStrong">Clear all</Typography>
            </Button>
          </HStack>
        </PopoverHeader>
        <PopoverBody>
          <VStack alignItems={'flex-start'}>
            {options.map((opt) => (
              <HStack key={opt.ref}>
                <Checkbox
                  isChecked={!!multiSelect.includes(opt.ref)}
                  onChange={() => {
                    const id = multiSelect.find((id) => id === opt.ref);
                    if (id) {
                      const newMultiSelect = multiSelect.filter((id) => id !== opt.ref);
                      setMultiSelect(newMultiSelect);
                      setConditions((prev) => [prev[0], ...newMultiSelect]);
                    } else {
                      setMultiSelect([...multiSelect, opt.ref]);
                      setConditions((prev) => [...prev, opt.ref]);
                    }
                  }}
                />
                <Typography variant="bodyStrong">{opt.label}</Typography>
              </HStack>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
